import { useEffect, useMemo, useRef, useState } from "react";
import { browseLibraryDocuments, loadDocument, loadExpressions } from "../lib/api";
import RevenueCodeTree from "./RevenueCodeTree";
import { ArrowIcon } from "./Icons";
import {
  displayDate,
  gregorianYear,
  legalHierarchies,
  typeLabel,
} from "../lib/legalTaxonomy";
import { useLocale } from "../lib/LocaleProvider";

export default function BrowseNavigator({
  catalog,
  catalogBusy,
    catalogError,
    selectedId,
    language = "th",
    onSelect,
    onCatalogRetry,
    notice = "",
}) {
  const { locale, text } = useLocale();
  const hierarchies = useMemo(() => legalHierarchies(locale), [locale]);
  const [hierarchyId, setHierarchyId] = useState("");
  const [axis, setAxis] = useState("type");
  const [documentType, setDocumentType] = useState("");
  const [yearBe, setYearBe] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [errorKey, setError] = useState("");
  const error = errorKey ? text(errorKey) : "";
  const [revenueCodeDocument, setRevenueCodeDocument] = useState(null);
  const [revenueCodeBundle, setRevenueCodeBundle] = useState(null);
  const [revenueCodeSourceBundle, setRevenueCodeSourceBundle] = useState(null);
  const [revenueCodeLanguage, setRevenueCodeLanguage] = useState(null);
  const browseRequestGeneration = useRef(0);

  useEffect(() => {
    if (
      documentType !== "RC" ||
      (revenueCodeBundle && revenueCodeLanguage === language)
    ) return;
    let cancelled = false;
    setRevenueCodeDocument(null);
    setRevenueCodeBundle(null);
    setRevenueCodeSourceBundle(null);
    setBusy(true);
    setError("");
    browseLibraryDocuments({ language, documentType: "RC" })
      .then(async (response) => {
        const result = response[0];
        if (!result) throw new Error("Revenue Code expression missing");
        const nextBundle = await loadDocument(result.expression_id);
        let sourceBundle = nextBundle;
        if (nextBundle.expression.language !== "th") {
          const companions = await loadExpressions(result.work_id);
          const thai = companions.expressions.find(item => item.language === "th");
          if (thai) sourceBundle = await loadDocument(thai.expression_id);
        }
        if (!cancelled) {
          setRevenueCodeDocument(result);
          setRevenueCodeBundle(nextBundle);
          setRevenueCodeSourceBundle(sourceBundle);
          setRevenueCodeLanguage(language);
        }
      })
      .catch(() => {
        if (!cancelled) setError("browse.revenueError");
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => { cancelled = true; };
  }, [documentType, language, revenueCodeBundle, revenueCodeLanguage, text]);

  const types = useMemo(() => {
    if (axis === "year") {
      return (catalog?.document_types || []).filter(item =>
        item.years?.some(year => String(year.year_be ?? "undated") === String(yearBe)),
      );
    }
    const counts = new Map(
      (catalog?.document_types || []).map((item) => [item.document_type, item]),
    );
    const hierarchy = hierarchies.find((item) => item.id === hierarchyId);
    return (hierarchy?.codes || []).map((code) => counts.get(code)).filter(Boolean);
  }, [axis, catalog, hierarchyId, hierarchies, yearBe]);

  const years = useMemo(() => [...new Set(
    (catalog?.document_types || []).flatMap(item => (item.years || []).map(year => year.year_be == null ? "undated" : Number(year.year_be))),
  )].sort((a, b) => a === "undated" ? 1 : b === "undated" ? -1 : b - a), [catalog]);

  const selectedType = (catalog?.document_types || []).find(
    (item) => item.document_type === documentType,
  );

  function openDocuments(nextYear) {
    setError("");
    setYearBe(nextYear);
    setDocuments([]);
    setListOpen(true);
  }

  useEffect(() => {
    if (!listOpen || !documentType || documentType === "RC") {
      if (documentType !== "RC") setBusy(false);
      return undefined;
    }
    const requestId = ++browseRequestGeneration.current;
    let current = true;
    setDocuments([]);
    setBusy(true);
    setError("");
    const undated = yearBe === "undated";
    browseLibraryDocuments({
      language,
      documentType,
      yearBe: undated || !yearBe ? undefined : yearBe,
    })
      .then((response) => {
        if (!current || requestId !== browseRequestGeneration.current) return;
        setDocuments(undated ? response.filter((item) => item.year_be == null) : response);
      })
      .catch(() => {
        if (!current || requestId !== browseRequestGeneration.current) return;
        setDocuments([]);
        setError("browse.listError");
      })
      .finally(() => {
        if (current && requestId === browseRequestGeneration.current) setBusy(false);
      });
    return () => {
      current = false;
      if (requestId === browseRequestGeneration.current) browseRequestGeneration.current += 1;
    };
  }, [documentType, language, listOpen, yearBe]);

  function resetToRoot() {
    setError("");
    setHierarchyId("");
    setDocumentType("");
    setYearBe("");
    setListOpen(false);
    setDocuments([]);
  }

  function openHierarchy(id) {
    setError("");
    setHierarchyId(id);
    setDocumentType("");
    setYearBe("");
    setListOpen(false);
    setDocuments([]);
  }

  function openType(code) {
    setError("");
    setDocumentType(code);
    if (axis !== "year") setYearBe("");
    setListOpen(axis === "year" && code !== "RC");
    setDocuments([]);
  }

  function goBack() {
    setError("");
    if (listOpen) {
      setListOpen(false);
      setDocuments([]);
      if (axis === "year") setDocumentType("");
      else setYearBe("");
    } else if (documentType) {
      setDocumentType("");
    } else if (axis === "year" && yearBe) {
      setYearBe("");
    } else {
      setHierarchyId("");
    }
  }

  const selectedHierarchy = hierarchies.find((item) => item.id === hierarchyId);
  const hasYearSelection = listOpen || (axis === "year" && Boolean(yearBe));
  return (
    <aside className="results-panel browse-panel" aria-label={text("browse.aria")} aria-busy={catalogBusy || busy}>
      <div className="browse-heading">
        <div>
          <h2>{text("browse.title")}</h2>
        </div>
        {Boolean(hierarchyId || documentType || documents.length || (axis === "year" && yearBe)) && (
          <button className="browse-back" type="button" onClick={goBack}>
            {text("common.back")}
          </button>
        )}
      </div>
      <nav className="browse-axis" aria-label={text("browse.paths")}>
        {["type", "year"].map(value => (
          <button key={value} type="button" aria-pressed={axis === value}
            onClick={() => { resetToRoot(); setAxis(value); }}>
            {text(value === "type" ? "results.byType" : "results.byYear")}
          </button>
        ))}
      </nav>
      <nav className="browse-breadcrumbs" aria-label={text("browse.breadcrumb")}>
        <button type="button" onClick={resetToRoot}>{text(axis === "year" ? "results.byYear" : "browse.root")}</button>
        {selectedHierarchy && (
          <>
            <span aria-hidden="true">/</span>
            <button type="button" onClick={() => openHierarchy(selectedHierarchy.id)}>
              {selectedHierarchy.title}
            </button>
          </>
        )}
        {axis === "year" && yearBe && (
          <>
            <span aria-hidden="true">/</span>
            <button type="button" onClick={() => { setDocumentType(""); setListOpen(false); setDocuments([]); setError(""); }}>
              {yearBe === "undated" ? text("browse.undatedYear") : locale === "en" ? gregorianYear(null, yearBe) : `พ.ศ. ${yearBe}`}
            </button>
          </>
        )}
        {documentType && (
          <>
            <span aria-hidden="true">/</span>
            <button type="button" onClick={() => openType(documentType)}>
              {typeLabel(documentType, locale)}
            </button>
          </>
        )}
        {axis !== "year" && hasYearSelection && (
          <>
            <span aria-hidden="true">/</span>
            <span>
              {yearBe === "undated"
                ? text("browse.undatedYear")
                : yearBe
                  ? locale === "en" ? gregorianYear(null, yearBe) : `พ.ศ. ${yearBe}`
                  : text("header.allYears")}
            </span>
          </>
        )}
      </nav>

      {(catalogBusy || busy) && <p className="browse-state">{text("browse.loading")}</p>}
      {(catalogError || error) && <p className="error-message">{catalogError || error}</p>}
      {catalogError && <button className="browse-retry" type="button" onClick={onCatalogRetry}>{text("browse.retry")}</button>}
      {catalogBusy && <div className="browse-skeleton" aria-hidden="true">{[0, 1, 2, 3].map(item => <span key={item} />)}</div>}
      {notice && <p className="browse-notice" role="status">{notice}</p>}

      {!catalogBusy && !catalogError && axis === "type" && !hierarchyId && (
        <ol className="browse-options hierarchy-options">
          {hierarchies.map((hierarchy) => {
            return (
              <li key={hierarchy.id}>
                <button type="button" onClick={() => openHierarchy(hierarchy.id)}>
                  <span><strong>{hierarchy.title}</strong><small>{hierarchy.description}</small></span>
                  <ArrowIcon />
                </button>
              </li>
            );
          })}
        </ol>
      )}

      {!catalogBusy && !catalogError && axis === "year" && !yearBe && (
        <ol className="browse-options year-options">
          {years.map(year => (
            <li key={year}>
              <button type="button" data-browse-year={year} onClick={() => { setYearBe(year); setDocumentType(""); setListOpen(false); }}>
                <span><strong>{year === "undated" ? text("browse.undatedYear") : locale === "en" ? gregorianYear(null, year) : `พ.ศ. ${year}`}</strong></span>
                <ArrowIcon />
              </button>
            </li>
          ))}
        </ol>
      )}

      {(hierarchyId || (axis === "year" && yearBe)) && !documentType && (
        <ol className="browse-options">
          {types.map((item) => (
            <li key={item.document_type}>
              <button type="button" data-browse-type={item.document_type} onClick={() => openType(item.document_type)}>
                <span>
                  <strong>{typeLabel(item.document_type, locale)}</strong>
                  {axis === "year" && <small>{text("browse.yearHelp")}</small>}
                </span>
                <ArrowIcon />
              </button>
            </li>
          ))}
        </ol>
      )}
      {!catalogBusy && !catalogError && (hierarchyId || (axis === "year" && yearBe)) && !documentType && types.length === 0 && (
        <p className="browse-state">{text("results.empty")}</p>
      )}

      {documentType === "RC" && !busy && (
        <RevenueCodeTree
          bundle={revenueCodeBundle}
          sourceBundle={revenueCodeSourceBundle}
          documentResult={revenueCodeDocument}
          onOpenSection={(document, sequence) => onSelect(document, true, sequence)}
        />
      )}

      {axis === "type" && documentType && documentType !== "RC" && !listOpen && !busy && (
        <ol className="browse-options year-options">
          <li>
            <button type="button" onClick={() => openDocuments("")}>
              <span><strong>{text("browse.allYears")}</strong><small>{typeLabel(documentType, locale)}</small></span>
            </button>
          </li>
          {(selectedType?.years || []).map((item) => {
            const year = item.year_be == null ? "undated" : item.year_be;
            return (
              <li key={year}>
                <button type="button" onClick={() => openDocuments(year)}>
                  <span>
                    <strong>{item.year_be ? locale === "en" ? gregorianYear(null, item.year_be) : `พ.ศ. ${item.year_be}` : text("browse.undatedYear")}</strong>
                    <small>{item.year_be ? text("browse.yearHelp") : text("browse.undatedHelp")}</small>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}

      {documents.length > 0 && (
        <ol className="browse-documents">
          {documents.map((document) => (
            <li key={document.expression_id}>
              <button
                type="button"
                className={selectedId === document.expression_id ? "selected" : ""}
                onClick={() => onSelect(document)}
              >
                <span className="browse-document-meta">
                  {typeLabel(document.document_type, locale)}
                  {displayDate(document.issued_date, document.year_be, locale) && (
                    <> · {displayDate(document.issued_date, document.year_be, locale)}</>
                  )}
                </span>
                <strong>{document.title}</strong>
                {document.official_number && <small>{document.official_number}</small>}
                {locale === "en" && document.language === "th" && (
                  <small>{text("reader.thaiOriginalOnly")}</small>
                )}
              </button>
            </li>
          ))}
        </ol>
      )}
      {listOpen && documentType !== "RC" && documents.length === 0 && !busy && !error && (
        <p className="browse-state">{text("results.empty")}</p>
      )}
    </aside>
  );
}
